import { Ref } from 'vue';
import {
  UtilizationPrice,
  DailyUtilization,
  TimeWindow,
  UtilizationRange,
} from '@/api';
import { dynPricingDayOfWeek } from '@/utils/dateUtils';
import { useToast } from 'vue-toastification';

interface EditResult {
  operationType: string;
  events: DailyUtilization[];
}

interface OverlapValidationResult {
  daysWithOverlaps: number[];
  daysWithoutOverlaps: number[];
}

export function useTimeWindowEdit(
  workingData: Ref<UtilizationPrice[]>,
  lastTempId: Ref<number>,
  priceWindowId: number | undefined,
  isOverlappingFn: (
    event: UtilizationPrice,
    existing: UtilizationPrice[]
  ) => boolean
) {
  const toast = useToast();
  const DEFAULT_START = '1900-01-01T00:00:00';
  const DEFAULT_END = '1900-01-01T23:59:59';

  function handleCreateTimeWindow(result: EditResult): void {
    result.events.forEach((dailyUtilization) => {
      dailyUtilization.timeWindows?.forEach((timeWindow) => {
        const dayDynPricing = dynPricingDayOfWeek(
          dailyUtilization.dayOfWeek ?? 0
        );
        const newEvent: UtilizationPrice = {
          dayOfTheWeek: dayDynPricing,
          startTime: timeWindow.startTime,
          endTime: timeWindow.endTime,
        };

        if (!isOverlappingFn(newEvent, workingData.value)) {
          addUtilizationRanges(timeWindow, dayDynPricing);
        } else {
          toast.error(
            'Cannot create time window: Overlaps with existing time window'
          );
        }
      });
    });
  }

  function validateEditOverlaps(
    result: EditResult,
    sourceDay: number,
    sourceRanges: UtilizationPrice[],
    sourceDayIncluded: boolean
  ): OverlapValidationResult {
    const daysWithOverlaps: number[] = [];
    const daysWithoutOverlaps: number[] = [];

    // Validate source day first
    if (
      sourceDayIncluded &&
      hasSourceDayOverlap(result, sourceDay, sourceRanges)
    ) {
      daysWithOverlaps.push(sourceDay);
    }

    // Validate other target days
    const targetDays = result.events
      .map((e) => dynPricingDayOfWeek(e.dayOfWeek ?? 0))
      .filter((day) => day !== sourceDay);

    targetDays.forEach((targetDay) => {
      if (hasTargetDayOverlap(result, targetDay)) {
        daysWithOverlaps.push(targetDay);
      } else {
        daysWithoutOverlaps.push(targetDay);
      }
    });

    return { daysWithOverlaps, daysWithoutOverlaps };
  }

  function applySourceDayEdit(
    timeWindow: TimeWindow,
    sourceDay: number,
    sourceRanges: UtilizationPrice[],
    shouldOverwrite: boolean
  ): void {
    if (shouldOverwrite) {
      removeOverlappingEvents(timeWindow, sourceDay, sourceRanges);
    }

    updateExistingRanges(timeWindow, sourceRanges);
    addNewRanges(timeWindow, sourceRanges, sourceDay);
    removeExtraRanges(timeWindow, sourceRanges, sourceDay);
  }

  function copyToTargetDays(
    result: EditResult,
    targetDays: number[],
    daysToOverwrite: number[],
    sourceDay: number
  ): void {
    const daysToCopy = targetDays.filter((day) => day !== sourceDay);

    daysToCopy.forEach((targetDay) => {
      const targetEvent = result.events.find(
        (e) => dynPricingDayOfWeek(e.dayOfWeek ?? 0) === targetDay
      );

      if (!targetEvent?.timeWindows?.[0]) return;

      const timeWindow = targetEvent.timeWindows[0];

      if (daysToOverwrite.includes(targetDay)) {
        removeOverlappingEvents(timeWindow, targetDay, []);
      }

      addUtilizationRanges(timeWindow, targetDay);
    });
  }

  function handleDeleteTimeWindow(result: EditResult): void {
    result.events.forEach((dailyUtilization) => {
      dailyUtilization.timeWindows?.forEach((timeWindow) => {
        timeWindow.utilizationRanges?.forEach((utilization) => {
          deleteEventById(Number(utilization.id));
        });
      });
    });
  }

  function handleCopyTimeWindow(result: EditResult): void {
    result.events.forEach((dailyUtilization) => {
      dailyUtilization.timeWindows?.forEach((timeWindow) => {
        const dayDynPricing = dynPricingDayOfWeek(
          dailyUtilization.dayOfWeek ?? 0
        );
        const sourceIds = new Set(
          timeWindow.utilizationRanges?.map((u) => u.id) ?? []
        );

        workingData.value = workingData.value.filter(
          (event) =>
            !(sourceIds.has(event.id) && event.dayOfTheWeek === dayDynPricing)
        );

        const testEvent: UtilizationPrice = {
          dayOfTheWeek: dayDynPricing,
          startTime: timeWindow.startTime
            ? `1900-01-01T${timeWindow.startTime}`
            : undefined,
          endTime: timeWindow.endTime
            ? `1900-01-01T${timeWindow.endTime}`
            : undefined,
        };

        const eventsToCheck = workingData.value.filter(
          (e) => !sourceIds.has(e.id)
        );

        if (!isOverlappingFn(testEvent, eventsToCheck)) {
          addUtilizationRanges(timeWindow, dayDynPricing);
        } else {
          toast.error(
            'Cannot create time window: Overlaps with existing time window'
          );
        }
      });
    });
  }

  // Helper functions
  function hasSourceDayOverlap(
    result: EditResult,
    sourceDay: number,
    sourceRanges: UtilizationPrice[]
  ): boolean {
    const sourceDayEvent = result.events.find(
      (e) => dynPricingDayOfWeek(e.dayOfWeek ?? 0) === sourceDay
    );

    if (!sourceDayEvent?.timeWindows?.[0]) return false;

    const testEvent: UtilizationPrice = {
      dayOfTheWeek: sourceDay,
      startTime: sourceDayEvent.timeWindows[0].startTime
        ? `1900-01-01T${sourceDayEvent.timeWindows[0].startTime}`
        : undefined,
      endTime: sourceDayEvent.timeWindows[0].endTime
        ? `1900-01-01T${sourceDayEvent.timeWindows[0].endTime}`
        : undefined,
    };

    const otherEvents = workingData.value.filter(
      (data) =>
        data.dayOfTheWeek === sourceDay &&
        !sourceRanges.some((sr) => sr.id === data.id)
    );

    return isOverlappingFn(testEvent, otherEvents);
  }

  function hasTargetDayOverlap(result: EditResult, targetDay: number): boolean {
    const targetEvent = result.events.find(
      (e) => dynPricingDayOfWeek(e.dayOfWeek ?? 0) === targetDay
    );

    if (!targetEvent?.timeWindows?.[0]) return false;

    const testEvent: UtilizationPrice = {
      dayOfTheWeek: targetDay,
      startTime: targetEvent.timeWindows[0].startTime
        ? `1900-01-01T${targetEvent.timeWindows[0].startTime}`
        : undefined,
      endTime: targetEvent.timeWindows[0].endTime
        ? `1900-01-01T${targetEvent.timeWindows[0].endTime}`
        : undefined,
    };

    const existingEvents = workingData.value.filter(
      (data) => data.dayOfTheWeek === targetDay
    );

    return isOverlappingFn(testEvent, existingEvents);
  }

  function removeOverlappingEvents(
    timeWindow: TimeWindow,
    dayOfWeek: number,
    excludeRanges: UtilizationPrice[]
  ): void {
    const testEvent: UtilizationPrice = {
      dayOfTheWeek: dayOfWeek,
      startTime: timeWindow.startTime
        ? `1900-01-01T${timeWindow.startTime}`
        : undefined,
      endTime: timeWindow.endTime
        ? `1900-01-01T${timeWindow.endTime}`
        : undefined,
    };

    const excludeIds = new Set(excludeRanges.map((r) => r.id));

    workingData.value = workingData.value.filter((data) => {
      if (data.dayOfTheWeek !== dayOfWeek) return true;
      if (excludeIds.has(data.id)) return true;

      const dataStart = new Date(data.startTime ?? DEFAULT_START);
      const dataEnd = new Date(data.endTime ?? DEFAULT_END);
      const testStart = new Date(testEvent.startTime ?? DEFAULT_START);
      const testEnd = new Date(testEvent.endTime ?? DEFAULT_END);

      return !(testStart < dataEnd && testEnd > dataStart);
    });
  }

  function updateExistingRanges(
    timeWindow: TimeWindow,
    sourceRanges: UtilizationPrice[]
  ): void {
    const updatedRangesById = new Map(
      timeWindow.utilizationRanges?.map((r: UtilizationRange) => [r.id, r])
    );

    sourceRanges.forEach((sourceRange) => {
      const updatedRange = updatedRangesById.get(sourceRange.id);
      if (updatedRange) {
        Object.assign(sourceRange, {
          timeWindowDescription: timeWindow.timeWindowDescription,
          startTime: timeWindow.startTime
            ? `1900-01-01T${timeWindow.startTime}`
            : undefined,
          endTime: timeWindow.endTime
            ? `1900-01-01T${timeWindow.endTime}`
            : undefined,
          low: updatedRange.low,
          high: updatedRange.high,
          pricePercentage: updatedRange.pricePercentage,
          priceAmount: updatedRange.priceAmount,
          usePercent: updatedRange.usePercent,
        });
      }
    });
  }

  function addNewRanges(
    timeWindow: TimeWindow,
    sourceRanges: UtilizationPrice[],
    dayOfWeek: number
  ): void {
    if (
      !timeWindow.utilizationRanges ||
      timeWindow.utilizationRanges.length <= sourceRanges.length
    )
      return;

    for (
      let i = sourceRanges.length;
      i < timeWindow.utilizationRanges.length;
      i++
    ) {
      const newRange = timeWindow.utilizationRanges[i];
      workingData.value.push({
        ...newRange,
        id: lastTempId.value--,
        priceWindowId,
        dayOfTheWeek: dayOfWeek,
        timeWindowDescription: timeWindow.timeWindowDescription,
        startTime: timeWindow.startTime
          ? `1900-01-01T${timeWindow.startTime}`
          : undefined,
        endTime: timeWindow.endTime
          ? `1900-01-01T${timeWindow.endTime}`
          : undefined,
      });
    }
  }

  function removeExtraRanges(
    timeWindow: TimeWindow,
    sourceRanges: UtilizationPrice[],
    dayOfWeek: number
  ): void {
    if (
      !timeWindow.utilizationRanges ||
      timeWindow.utilizationRanges.length >= sourceRanges.length
    )
      return;

    const sourceIds = new Set(sourceRanges.map((r) => r.id));
    const keepIds = new Set(timeWindow.utilizationRanges.map((r) => r.id));

    workingData.value = workingData.value.filter((data) => {
      if (data.dayOfTheWeek !== dayOfWeek) return true;
      if (!sourceIds.has(data.id)) return true;
      return keepIds.has(data.id);
    });
  }

  function addUtilizationRanges(
    timeWindow: TimeWindow,
    dayOfWeek: number
  ): void {
    timeWindow.utilizationRanges?.forEach((utilization: UtilizationRange) => {
      workingData.value.push({
        ...utilization,
        id: lastTempId.value--,
        priceWindowId,
        dayOfTheWeek: dayOfWeek,
        timeWindowDescription: timeWindow.timeWindowDescription,
        startTime: timeWindow.startTime
          ? `1900-01-01T${timeWindow.startTime}`
          : undefined,
        endTime: timeWindow.endTime
          ? `1900-01-01T${timeWindow.endTime}`
          : undefined,
      });
    });
  }

  function deleteEventById(eventId: number): void {
    workingData.value = workingData.value.filter(
      (event) => event.id !== eventId
    );
  }

  return {
    handleCreateTimeWindow,
    validateEditOverlaps,
    applySourceDayEdit,
    copyToTargetDays,
    handleDeleteTimeWindow,
    handleCopyTimeWindow,
  };
}
